import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { propertiesTypeApi, propertiesApi } from '../../services/adminApi';
import '../../styles/AdminPages.css';
import './PropertyViewPage.css';

const API_URL = 'https://api.tannit.uk';

const displayValue = (value, suffix = '') => {
  if (value === null || value === undefined || value === '') {
    return 'N/A';
  }
  return suffix ? `${value}${suffix}` : value;
};

const sectionNameToCategory = {
  'Property details': 'Property Details',
  'Environmental features': 'Sustainability Features',
  'Energy consumption': 'Energy',
  'Financial metrics': 'Financial Information',
  'Sustainability': 'Sustainability Features',
  'Gain': 'Financial Information'
};

const canonicalCategories = ['Energy', 'Property Details', 'Sustainability Features', 'Financial Information'];

const energyLabelColors = {
  'A': '#00a651',
  'B': '#50b848',
  'C': '#bdd62e',
  'D': '#fff200',
  'E': '#fcb813',
  'F': '#f37021',
  'G': '#ed1c24'
};

const PropertyViewPage = () => {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [propertyTypes, setPropertyTypes] = useState([]);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('general');
  const [selectedImage, setSelectedImage] = useState(0);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [propertyResponse, typesResponse, sectionsResponse] = await Promise.all([
          axios.get(`${API_URL}/Property/GetPropertyWithTags/${propertyId}`),
          propertiesTypeApi.getAll(),
          axios.get(`${API_URL}/Section`)
        ]);
        
        const propertyData = propertyResponse?.data?.data?.[0] || propertyResponse?.data?.[0] || propertyResponse?.data;
        setProperty(Array.isArray(propertyData) ? propertyData[0] : propertyData);
        
        const types = typesResponse?.data?.[0] || typesResponse?.data || typesResponse || [];
        setPropertyTypes(Array.isArray(types) ? types : []);

        const sectionsData = sectionsResponse?.data?.data?.[0] || sectionsResponse?.data?.data || sectionsResponse?.data || [];
        setSections(Array.isArray(sectionsData) ? sectionsData : []);
      } catch (err) {
        console.error('Error fetching property:', err);
        setError(err.message || 'Failed to load property');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [propertyId]);

  const handleDelete = async () => {
    const result = await Swal.fire({
      title: 'Delete Property?',
      text: `Are you sure you want to delete "${property?.propertyName}"? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        setDeleting(true);
        await propertiesApi.delete(propertyId);
        Swal.fire({
          title: 'Deleted!',
          text: 'Property has been deleted successfully.',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        });
        navigate('/admin/properties');
      } catch (err) {
        console.error('Error deleting property:', err);
        Swal.fire({
          title: 'Error!',
          text: err.message || 'Failed to delete property',
          icon: 'error'
        });
      } finally {
        setDeleting(false);
      }
    }
  };

  const getSectionName = (sectionId) => {
    const section = sections.find(s => s.id === sectionId);
    return section?.sectionName || 'Other';
  };

  const normalizeTags = (tags) => {
    const normalized = {
      'Energy': [],
      'Property Details': [],
      'Sustainability Features': [],
      'Financial Information': [],
      'Other': {}
    };

    tags.forEach(tag => {
      const sectionName = getSectionName(tag.sectionId);
      const mappedCategory = sectionNameToCategory[sectionName];
      
      if (mappedCategory && canonicalCategories.includes(mappedCategory)) {
        normalized[mappedCategory].push(tag);
      } else {
        if (!normalized['Other'][sectionName]) {
          normalized['Other'][sectionName] = [];
        }
        normalized['Other'][sectionName].push(tag);
      }
    });

    return normalized;
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="property-view-loading">
          <div className="admin-spinner"></div>
          <span>Loading property details...</span>
        </div>
      </div>
    );
  }

  if (error || !property) {
    return (
      <div className="admin-page">
        <div className="property-view-error">
          <h2>Error</h2>
          <p>{error || 'Property not found'}</p>
          <button className="admin-btn primary" onClick={() => navigate('/admin/properties')}>
            Back to Properties
          </button>
        </div>
      </div>
    );
  }

  const address = property.address || {};
  const images = property.images || [];
  const tags = property.tags || [];
  const propertyType = propertyTypes.find(pt => pt.propertyTypeId === property.propertyTypeId);
  
  const addressString = [
    address.streetAddress,
    address.city,
    address.state,
    address.postalCode
  ].filter(Boolean).join(', ');

  const price = property.price || 0;
  const priceInTannit = price / 10;

  const normalizedTags = normalizeTags(tags);

  const profitabilityTag = normalizedTags['Financial Information'].find(tag => tag.label === 'Target Profitability');
  const returnedTag = normalizedTags['Financial Information'].find(tag => tag.label === 'Returned Income');

  const renderTagValue = (tag) => {
    if (tag.boolValue !== null && tag.boolValue !== undefined) {
      return tag.boolValue ? 'Yes' : 'No';
    }
    if (tag.decimalValue !== null && tag.decimalValue !== undefined) {
      return `${tag.decimalValue}${tag.unit ? ` ${tag.unit}` : ''}`;
    }
    if (tag.stringValue) {
      return tag.stringValue;
    }
    return 'N/A';
  };

  const isEnergyLabel = (label) => ['A', 'B', 'C', 'D', 'E', 'F', 'G'].includes(label?.trim());

  return (
    <div className="admin-page property-view-page">
      <div className="property-view-header">
        <div className="property-view-nav">
          <button 
            className="back-btn"
            onClick={() => navigate('/admin/properties')}
          >
            <span>←</span> Back to Properties
          </button>
          <div className="property-view-actions">
            <button 
              className="admin-btn primary"
              onClick={() => navigate(`/admin/properties/edit/${propertyId}`)}
            >
              Edit Property
            </button>
            <button 
              className="admin-btn danger"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? 'Deleting...' : 'Delete Property'}
            </button>
          </div>
        </div>
      </div>

      <div className="property-view-content">
        <div className="property-view-main">
          <div className="property-gallery">
            {images.length > 0 ? (
              <>
                <div className="gallery-main">
                  {images[selectedImage]?.type === 'video' ? (
                    <video controls className="main-media">
                      <source src={images[selectedImage].url} type="video/mp4" />
                    </video>
                  ) : (
                    <img 
                      src={images[selectedImage]?.url || '/assets/images/placeholder.png'} 
                      alt={property.propertyName}
                      className="main-media"
                    />
                  )}
                </div>
                {images.length > 1 && (
                  <div className="gallery-thumbnails">
                    {images.map((img, index) => (
                      <div 
                        key={img.imageId || index}
                        className={`thumbnail ${index === selectedImage ? 'active' : ''}`}
                        onClick={() => setSelectedImage(index)}
                      >
                        {img.type === 'video' ? (
                          <div className="video-thumb">
                            <span>▶</span>
                          </div>
                        ) : (
                          <img src={img.url} alt={`Thumbnail ${index + 1}`} />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="no-images">
                <span>📷</span>
                <p>No images available</p>
              </div>
            )}
          </div>

          <div className="property-tabs">
            <div className="tabs-nav">
              <button 
                className={activeTab === 'general' ? 'active' : ''}
                onClick={() => setActiveTab('general')}
              >
                General
              </button>
              <button 
                className={activeTab === 'details' ? 'active' : ''}
                onClick={() => setActiveTab('details')}
              >
                Property Details
              </button>
              <button 
                className={activeTab === 'sustainability' ? 'active' : ''}
                onClick={() => setActiveTab('sustainability')}
              >
                Sustainability
              </button>
              <button 
                className={activeTab === 'finance' ? 'active' : ''}
                onClick={() => setActiveTab('finance')}
              >
                Finance
              </button>
            </div>

            <div className="tab-content">
              {activeTab === 'general' && (
                <div className="tab-panel">
                  <h2>{displayValue(property.propertyName)}</h2>
                  <p className="property-address">
                    <span className="icon">📍</span> {displayValue(addressString) === 'N/A' ? 'N/A' : addressString}
                  </p>
                  <div className="property-description">
                    <h4>Description</h4>
                    <p>{displayValue(property.description || property.propertyDescription, '') || 'No description available'}</p>
                  </div>
                  <div className="property-meta">
                    <div className="meta-item">
                      <span className="label">Property Type</span>
                      <span className="value">{displayValue(propertyType?.propertyTypeName)}</span>
                    </div>
                    <div className="meta-item">
                      <span className="label">Created</span>
                      <span className="value">{property.createdAt ? new Date(property.createdAt).toLocaleDateString() : 'N/A'}</span>
                    </div>
                    <div className="meta-item">
                      <span className="label">Status</span>
                      <span className={`value status ${property.archived ? 'archived' : 'active'}`}>
                        {property.archived ? 'Archived' : 'Active'}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'details' && (
                <div className="tab-panel">
                  <h3>Property Details</h3>
                  <div className="tags-grid">
                    {normalizedTags['Property Details'].filter(tag => !isEnergyLabel(tag.label) && tag.unit !== 'group-1' && tag.unit !== 'group-2').map(tag => (
                      <div key={tag.tagId} className="tag-item">
                        <span className="tag-label">
                          {tag.fontAwesomeId && <i className={tag.fontAwesomeId} style={{ marginRight: '8px' }} />}
                          {displayValue(tag.label)}
                        </span>
                        <span className="tag-value">
                          {renderTagValue(tag)}
                        </span>
                      </div>
                    ))}
                    {normalizedTags['Property Details'].filter(tag => !isEnergyLabel(tag.label) && tag.unit !== 'group-1' && tag.unit !== 'group-2').length === 0 && (
                      <p className="no-data">No property details configured</p>
                    )}
                  </div>

                  <h3 style={{ marginTop: '30px' }}>Energy</h3>
                  <div className="energy-section">
                    {normalizedTags['Energy'].length > 0 ? (
                      <div className="energy-display">
                        {normalizedTags['Energy'].filter(tag => isEnergyLabel(tag.label) && tag.unit === 'group-1').map(tag => (
                          <div 
                            key={tag.tagId} 
                            className="energy-label-badge"
                            style={{ 
                              backgroundColor: energyLabelColors[tag.label.trim()],
                              color: '#fff',
                              padding: '12px 24px',
                              borderRadius: '8px',
                              fontWeight: '700',
                              fontSize: '24px',
                              display: 'inline-block',
                              marginRight: '10px'
                            }}
                          >
                            {tag.label}
                          </div>
                        ))}
                        {normalizedTags['Energy'].filter(tag => !isEnergyLabel(tag.label) || tag.unit !== 'group-1').map(tag => (
                          <div key={tag.tagId} className="energy-item">
                            <span className="energy-class">
                              {tag.fontAwesomeId && <i className={tag.fontAwesomeId} style={{ marginRight: '8px' }} />}
                              {displayValue(tag.label)}
                            </span>
                            <span className="energy-value">{renderTagValue(tag)}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="no-data">No energy data configured</p>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'sustainability' && (
                <div className="tab-panel">
                  <h3>Sustainability Features</h3>
                  <div className="tags-grid sustainability-grid">
                    {normalizedTags['Sustainability Features'].filter(tag => !isEnergyLabel(tag.label) && tag.unit !== 'group-1').map(tag => (
                      <div key={tag.tagId} className="tag-item feature">
                        <span className="tag-label">
                          {tag.fontAwesomeId && <i className={tag.fontAwesomeId} style={{ marginRight: '8px' }} />}
                          {displayValue(tag.label)}
                        </span>
                        <span className="tag-value">
                          {tag.stringValue ? tag.stringValue : (tag.boolValue === null || tag.boolValue === undefined ? 'N/A' : (tag.boolValue ? '✓ Yes' : '✗ No'))}
                        </span>
                      </div>
                    ))}
                    {normalizedTags['Sustainability Features'].filter(tag => !isEnergyLabel(tag.label) && tag.unit !== 'group-1').length === 0 && (
                      <p className="no-data">No sustainability features configured</p>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'finance' && (
                <div className="tab-panel">
                  <h3>Financial Information</h3>
                  <div className="finance-grid">
                    {normalizedTags['Financial Information'].filter(tag => tag.unit !== 'group-2').map(tag => (
                      <div key={tag.tagId} className="finance-item">
                        <span className="finance-label">
                          {tag.fontAwesomeId && <i className={tag.fontAwesomeId} style={{ marginRight: '8px' }} />}
                          {displayValue(tag.label)}
                        </span>
                        <span className="finance-value">{renderTagValue(tag)}</span>
                      </div>
                    ))}
                    {normalizedTags['Financial Information'].filter(tag => tag.unit !== 'group-2').length === 0 && (
                      <p className="no-data">No financial data configured</p>
                    )}
                  </div>
                  {normalizedTags['Financial Information'].filter(tag => tag.unit === 'group-2').length > 0 && (
                    <div className="credit-info" style={{ marginTop: '20px' }}>
                      <h4>Credit Options</h4>
                      {normalizedTags['Financial Information'].filter(tag => tag.unit === 'group-2').map(tag => (
                        <span key={tag.tagId} className="credit-badge">{tag.label}</span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="property-view-sidebar">
          <div className="price-card">
            <div className="price-header">
              <h3>Investment Details</h3>
            </div>
            <div className="price-body">
              <div className="price-main">
                <span className="currency">£</span>
                <span className="amount">{price ? price.toLocaleString() : 'N/A'}</span>
              </div>
              <div className="price-tannit">
                Converted to: <strong>{price ? `${priceInTannit.toLocaleString()} Tannits` : 'N/A'}</strong>
              </div>
              <div className="progress-section">
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: '0%' }}></div>
                </div>
                <div className="progress-label">
                  {price ? `${priceInTannit.toLocaleString()} Tannits remaining` : 'N/A'}
                </div>
              </div>
              <div className="stats-row">
                <div className="stat">
                  <span className="stat-label">Profitability</span>
                  <span className="stat-value">
                    {profitabilityTag && profitabilityTag.decimalValue !== null && profitabilityTag.decimalValue !== undefined
                      ? `${profitabilityTag.decimalValue}${profitabilityTag.unit || '%'}`
                      : 'N/A'}
                  </span>
                </div>
                <div className="stat">
                  <span className="stat-label">Returned</span>
                  <span className="stat-value">
                    {returnedTag && returnedTag.decimalValue !== null && returnedTag.decimalValue !== undefined
                      ? `${returnedTag.decimalValue}${returnedTag.unit || '%'}`
                      : 'N/A'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="location-card">
            <h3>Location</h3>
            <div className="location-details">
              <p><strong>Street:</strong> {displayValue(address.streetAddress)}</p>
              <p><strong>City:</strong> {displayValue(address.city)}</p>
              <p><strong>State:</strong> {displayValue(address.state)}</p>
              <p><strong>Postal Code:</strong> {displayValue(address.postalCode)}</p>
              <p className="coords">
                <strong>Coordinates:</strong> {address.latitude && address.longitude 
                  ? `${address.latitude}, ${address.longitude}` 
                  : 'N/A'}
              </p>
            </div>
          </div>

          <div className="tags-card">
            <h3>Other Tags</h3>
            <div className="tags-list">
              {Object.keys(normalizedTags['Other']).length > 0 ? (
                Object.entries(normalizedTags['Other']).map(([section, sectionTags]) => (
                  <div key={section} className="tag-section">
                    <h4>{section}</h4>
                    {sectionTags.map(tag => (
                      <div key={tag.tagId} className="tag-row">
                        <span>
                          {tag.fontAwesomeId && <i className={tag.fontAwesomeId} style={{ marginRight: '6px' }} />}
                          {displayValue(tag.label)}
                        </span>
                        <span>{renderTagValue(tag)}</span>
                      </div>
                    ))}
                  </div>
                ))
              ) : (
                <p className="no-data">No other tags configured</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyViewPage;
